import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Switch } from "@/shared/ui/switch";
import { Label } from "@/shared/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Slider } from "@/shared/ui/slider";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Separator } from "@/shared/ui/separator";
import { TimeRangeSelector, type TimeSelection } from "@/shared/ui/time-range-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  X,
  AlertTriangle,
  Bell,
  BellOff,
  Check,
  Activity,
  Loader2,
  Clock,
  Cpu,
  Hash,
  Percent,
  Timer,
  TrendingUp,
  Calendar,
  Settings,
  Gauge,
  Zap,
  ArrowLeft,
  ChevronDown,
  CalendarDays,
  RefreshCw
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { usePushNotifications } from "@/core/hooks/usePushNotifications";
import { useGlobalTimeRange } from "@/core/hooks/useGlobalTimeRange";

interface FieldMapping {
  id: string;
  station_id: string | null;
  datapoint: {
    field_name: string;
    display_name: string;
    unit?: { symbol: string } | null;
  } | null;
  exposed_mappings?: {
    exposed_value: {
      name: string;
    };
  }[];
  semanticKey?: string;
}

interface StationOption {
  id: string;
  name: string;
  areaName: string;
  lineName: string;
}

interface StationDetailViewProps {
  stationId: string;
  stationName: string;
  areaName: string;
  lineName: string;
  factoryId: string;
  onClose: () => void;
  allStations?: StationOption[];
  onStationChange?: (station: StationOption) => void;
}

interface LiveValue {
  value: string | number | null;
  timestamp: string | null;
}

interface InferenceHistoryPoint {
  timestamp: number;
  confidence: number;
  status: number;
  processingTime: number;
  serialNumber: string;
  modelType: string;
}

interface AlertSettings {
  enabled: boolean;
  confidenceAlert: boolean;
  confidenceThreshold: number;
  okRateAlert: boolean;
  okRateThreshold: number;
  processingTimeAlert: boolean;
  processingTimeThreshold: number;
  anomalyAlert: boolean;
}

const defaultAlertSettings: AlertSettings = {
  enabled: true,
  confidenceAlert: true,
  confidenceThreshold: 85,
  okRateAlert: true,
  okRateThreshold: 95,
  processingTimeAlert: false,
  processingTimeThreshold: 100,
  anomalyAlert: true,
};

// No longer needed - using global time range hook

// Load alert settings from localStorage
const loadAlertSettings = (stationId: string): AlertSettings => {
  try {
    const stored = localStorage.getItem(`alert-settings-${stationId}`);
    if (stored) {
      return { ...defaultAlertSettings, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error("Failed to load alert settings:", e);
  }
  return defaultAlertSettings;
};

// Mapping metric types to display info - these keys match exposed value names from mod_ai
const metricTypeConfig: Record<string, { label: string; icon: React.ReactNode; format?: (v: unknown) => string }> = {
  serial_number: { label: "Part / Serial Number", icon: <Hash className="h-4 w-4" /> },
  inference_timestamp: { label: "Timestamp", icon: <Clock className="h-4 w-4" /> },
  inference_status: { label: "Status", icon: <Check className="h-4 w-4" />, format: (v) => v === 0 || v === "0" ? "OK" : "NOK" },
  inference_confidence: { label: "Confidence", icon: <Percent className="h-4 w-4" />, format: (v) => `${(Number(v) * 100).toFixed(1)}%` },
  model_name: { label: "Model Name", icon: <Cpu className="h-4 w-4" /> },
  model_type: { label: "Model Type", icon: <Cpu className="h-4 w-4" /> },
  processing_time: { label: "Processing Time", icon: <Timer className="h-4 w-4" /> },
  prediction: { label: "Prediction", icon: <TrendingUp className="h-4 w-4" /> },
};

export const StationDetailView = ({
  stationId,
  stationName,
  areaName,
  lineName,
  factoryId,
  onClose,
  allStations = [],
  onStationChange,
}: StationDetailViewProps) => {
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  
  // Use global time range hook for consistent time selection across views
  const { timeSelection, setTimeSelection, dateRange } = useGlobalTimeRange();
  
  const [loading, setLoading] = useState(true);
  const [liveValues, setLiveValues] = useState<Record<string, LiveValue>>({});
  const [inferenceHistory, setInferenceHistory] = useState<InferenceHistoryPoint[]>([]);
  const [anomalyCount, setAnomalyCount] = useState(0);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [alertSettings, setAlertSettings] = useState<AlertSettings>(() => loadAlertSettings(stationId));
  const [alertsTriggered, setAlertsTriggered] = useState<Set<string>>(new Set());
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Manual refresh trigger
  
  // Push notifications hook
  const { sendLocalNotification, permission: pushPermission } = usePushNotifications();

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Persist alert settings
  useEffect(() => {
    localStorage.setItem(`alert-settings-${stationId}`, JSON.stringify(alertSettings));
  }, [alertSettings, stationId]);

  // Update alert settings
  const updateAlertSetting = useCallback(<K extends keyof AlertSettings>(key: K, value: AlertSettings[K]) => {
    setAlertSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Fetch AI production field mappings for this station
  useEffect(() => {
    const fetchMappings = async () => {
      setLoading(true);
      try {
        console.log('[StationDetailView] Fetching field mappings for:', { factoryId, stationId, stationName });
        
        const { data, error } = await supabase
          .schema('core')
          .from('field_mappings')
          .select(`
            id,
            station_id,
            datapoint:datapoints (
              field_name,
              display_name,
              unit:units (symbol)
            ),
            exposed_mappings:datapoint_exposed_value_mapping (
              exposed_value:exposed_value_definitions (
                name
              )
            )
          `)
          .eq('factory_id', factoryId)
          .eq('station_id', stationId)
          .eq('is_active', true);

        if (error) throw error;
        
        console.log('[StationDetailView] Raw field mappings from DB:', data);
        
        const mappings = (data as unknown as FieldMapping[]) || [];
        const allowedKeys = new Set(Object.keys(metricTypeConfig));
        
        console.log('[StationDetailView] Allowed AI metric keys:', Array.from(allowedKeys));
        
        const filteredMappings = mappings.filter(mapping => {
          const exposedName = mapping.exposed_mappings?.[0]?.exposed_value?.name;
          const fieldName = mapping.datapoint?.field_name;
          const matches = (exposedName && allowedKeys.has(exposedName)) || 
                 (fieldName && allowedKeys.has(fieldName));
          
          console.log('[StationDetailView] Mapping filter:', {
            mappingId: mapping.id,
            fieldName,
            exposedName,
            matches
          });
          
          return matches;
        }).map(mapping => ({
          ...mapping,
          semanticKey: mapping.exposed_mappings?.[0]?.exposed_value?.name || mapping.datapoint?.field_name
        }));
        
        console.log('[StationDetailView] Filtered mappings that will be used:', filteredMappings);
        
        setFieldMappings(filteredMappings);
      } catch (error) {
        console.error("Error fetching field mappings:", error);
        toast.error("Failed to load field mappings");
      } finally {
        setLoading(false);
      }
    };

    fetchMappings();
  }, [factoryId, stationId]);

  // Determine if we're in "live" mode (showing today only) - removed auto-polling, now manual refresh
  const isLiveMode = useMemo(() => {
    if (timeSelection.mode === 'preset' && timeSelection.preset === 'day') {
      const today = new Date();
      const refDay = timeSelection.refDate;
      return today.toDateString() === refDay.toDateString();
    }
    return false;
  }, [timeSelection]);

  // Fetch historical data when time range changes or manual refresh is triggered
  useEffect(() => {
    if (fieldMappings.length === 0) return;

    const fetchHistoricalData = async () => {
      setHistoricalLoading(true);
      try {
        const mappingIds = fieldMappings.map(m => m.id);

        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;

        // Prepare request body with date range from time selection
        const requestBody: any = {
          mapping_ids: mappingIds,
          historical_raw: true,
          startTime: dateRange.start.toISOString(),
          endTime: dateRange.end.toISOString(),
        };

        console.log('[StationDetailView] Fetching historical data:', requestBody);

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL || 'https://gnncezzuafxmxukrfhgb.supabase.co'}/functions/v1/influxdb-query`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.session.access_token}`,
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log('[StationDetailView] Historical data response:', result);
          
          if (result.data && Array.isArray(result.data)) {
            // Transform raw historical data to inference history format
            const history: InferenceHistoryPoint[] = result.data.map((record: any) => ({
              timestamp: new Date(record.timestamp).getTime(),
              confidence: record.confidence != null ? Number(record.confidence) * 100 : 0,
              status: Number(record.prediction) || 0,
              processingTime: Number(record.predictionTimeMs) || 0,
              serialNumber: String(record.serial_number || ''),
              modelType: String(record.modelType || ''),
            })).filter((item: InferenceHistoryPoint) => 
              // Filter out records that don't have essential data (confidence or timestamp)
              !isNaN(item.timestamp) && item.confidence !== null
            );

            // Sort by timestamp ascending for chart
            history.sort((a, b) => a.timestamp - b.timestamp);

            console.log('[StationDetailView] Transformed history:', history.length, 'records');
            setInferenceHistory(history);
          }
        } else {
          const errorText = await response.text();
          console.error('[StationDetailView] Historical data fetch failed:', response.status, errorText);
          toast.error("Failed to load historical data");
        }
      } catch (error) {
        console.error("Error fetching historical data:", error);
        toast.error("Failed to load historical data");
      } finally {
        setHistoricalLoading(false);
      }
    };

    fetchHistoricalData();
  }, [fieldMappings, dateRange, refreshTrigger]);

  const formatTime = (timestamp: number) => {
    return format(new Date(timestamp), "HH:mm:ss");
  };

  const formatDateTime = (timestamp: number) => {
    return format(new Date(timestamp), "MMM dd, HH:mm:ss");
  };

  const chartData = useMemo(() => {
    return inferenceHistory.map((point) => ({
      time: formatTime(point.timestamp),
      confidence: point.confidence,
      processingTime: point.processingTime,
      isAnomaly: point.status !== 0,
    }));
  }, [inferenceHistory]);

  const stats = useMemo(() => {
    if (inferenceHistory.length === 0) return null;
    const confidences = inferenceHistory.map(d => d.confidence);
    const processingTimes = inferenceHistory.map(d => d.processingTime);
    const anomalies = inferenceHistory.filter(d => d.status !== 0).length;
    return {
      avgConfidence: confidences.reduce((a, b) => a + b, 0) / confidences.length,
      minConfidence: Math.min(...confidences),
      maxConfidence: Math.max(...confidences),
      avgProcessingTime: processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length,
      count: inferenceHistory.length,
      okCount: inferenceHistory.length - anomalies,
      nokCount: anomalies,
      okRate: ((inferenceHistory.length - anomalies) / inferenceHistory.length * 100).toFixed(1),
    };
  }, [inferenceHistory]);

  // Check alert thresholds
  useEffect(() => {
    if (!alertSettings.enabled || !stats || inferenceHistory.length < 3) return;
    
    const okRate = parseFloat(stats.okRate);
    const lastEntry = inferenceHistory[inferenceHistory.length - 1];
    const currentConfidence = lastEntry?.confidence || 0;
    const currentProcessingTime = lastEntry?.processingTime || 0;

    // Check confidence threshold
    if (alertSettings.confidenceAlert && currentConfidence < alertSettings.confidenceThreshold) {
      if (!alertsTriggered.has('confidence')) {
        const message = `Low confidence: ${currentConfidence.toFixed(1)}% (threshold: ${alertSettings.confidenceThreshold}%)`;
        sendLocalNotification(`⚠️ ${stationName} - Low Confidence`, {
          body: message,
          tag: 'ai-alert-confidence',
          data: { url: '/ai-control' },
        });
        toast.warning(message);
        setAlertsTriggered(prev => new Set([...prev, 'confidence']));
      }
    } else {
      setAlertsTriggered(prev => {
        const next = new Set(prev);
        next.delete('confidence');
        return next;
      });
    }

    // Check OK rate threshold
    if (alertSettings.okRateAlert && okRate < alertSettings.okRateThreshold) {
      if (!alertsTriggered.has('okRate')) {
        const message = `Low OK rate: ${okRate}% (threshold: ${alertSettings.okRateThreshold}%)`;
        sendLocalNotification(`⚠️ ${stationName} - Low OK Rate`, {
          body: message,
          tag: 'ai-alert-okrate',
          data: { url: '/ai-control' },
        });
        toast.warning(message);
        setAlertsTriggered(prev => new Set([...prev, 'okRate']));
      }
    } else {
      setAlertsTriggered(prev => {
        const next = new Set(prev);
        next.delete('okRate');
        return next;
      });
    }

    // Check processing time threshold
    if (alertSettings.processingTimeAlert && currentProcessingTime > alertSettings.processingTimeThreshold) {
      if (!alertsTriggered.has('processingTime')) {
        const message = `Slow processing: ${currentProcessingTime.toFixed(0)}ms (threshold: ${alertSettings.processingTimeThreshold}ms)`;
        sendLocalNotification(`⚠️ ${stationName} - Slow Processing`, {
          body: message,
          tag: 'ai-alert-processing',
          data: { url: '/ai-control' },
        });
        toast.warning(message);
        setAlertsTriggered(prev => new Set([...prev, 'processingTime']));
      }
    } else {
      setAlertsTriggered(prev => {
        const next = new Set(prev);
        next.delete('processingTime');
        return next;
      });
    }
  }, [stats, alertSettings, inferenceHistory, alertsTriggered, sendLocalNotification, stationName]);

  const currentStatus = liveValues['inference_status']?.value;
  const isOK = currentStatus === 0 || currentStatus === "0";

  const formatValue = (metricType: string, value: any, unit: string | null) => {
    if (value === null || value === undefined) return '-';
    const config = metricTypeConfig[metricType];
    if (config?.format) return config.format(value);
    if (unit) return `${value} ${unit}`;
    return String(value);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9 shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mb-1">
                <span className="truncate">{areaName}</span>
                <span>/</span>
                <span className="truncate">{lineName}</span>
              </div>
              <h1 className="text-xl md:text-2xl font-bold truncate">{stationName}</h1>
              <p className="text-sm text-muted-foreground hidden sm:block">Monitor AI predictions</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!loading && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRefresh}
                  disabled={historicalLoading}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${historicalLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      {alertSettings.enabled ? (
                        <Bell className="h-4 w-4 text-warning" />
                      ) : (
                        <BellOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      Alerts
                      <Settings className="h-3 w-3 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Alert Settings</h4>
                          <p className="text-xs text-muted-foreground">Configure alert thresholds</p>
                        </div>
                        <Switch
                          checked={alertSettings.enabled}
                          onCheckedChange={(v) => updateAlertSetting('enabled', v)}
                        />
                      </div>
                      <Separator />
                      
                      {/* Anomaly Alert */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-destructive" />
                            <Label className="text-sm">Anomaly (NOK) Alert</Label>
                          </div>
                          <Switch
                            checked={alertSettings.anomalyAlert}
                            onCheckedChange={(v) => updateAlertSetting('anomalyAlert', v)}
                            disabled={!alertSettings.enabled}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground ml-6">Alert when a NOK status is detected</p>
                      </div>

                      {/* Confidence Alert */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Gauge className="h-4 w-4 text-warning" />
                            <Label className="text-sm">Low Confidence Alert</Label>
                          </div>
                          <Switch
                            checked={alertSettings.confidenceAlert}
                            onCheckedChange={(v) => updateAlertSetting('confidenceAlert', v)}
                            disabled={!alertSettings.enabled}
                          />
                        </div>
                        <div className="flex items-center gap-3 ml-6">
                          <span className="text-xs text-muted-foreground w-16">Below</span>
                          <Slider
                            value={[alertSettings.confidenceThreshold]}
                            onValueChange={([v]) => updateAlertSetting('confidenceThreshold', v)}
                            min={50}
                            max={99}
                            step={1}
                            disabled={!alertSettings.enabled || !alertSettings.confidenceAlert}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{alertSettings.confidenceThreshold}%</span>
                        </div>
                      </div>

                      {/* OK Rate Alert */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Percent className="h-4 w-4 text-primary" />
                            <Label className="text-sm">Low OK Rate Alert</Label>
                          </div>
                          <Switch
                            checked={alertSettings.okRateAlert}
                            onCheckedChange={(v) => updateAlertSetting('okRateAlert', v)}
                            disabled={!alertSettings.enabled}
                          />
                        </div>
                        <div className="flex items-center gap-3 ml-6">
                          <span className="text-xs text-muted-foreground w-16">Below</span>
                          <Slider
                            value={[alertSettings.okRateThreshold]}
                            onValueChange={([v]) => updateAlertSetting('okRateThreshold', v)}
                            min={50}
                            max={99}
                            step={1}
                            disabled={!alertSettings.enabled || !alertSettings.okRateAlert}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium w-12">{alertSettings.okRateThreshold}%</span>
                        </div>
                      </div>

                      {/* Processing Time Alert */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-primary" />
                            <Label className="text-sm">Slow Processing Alert</Label>
                          </div>
                          <Switch
                            checked={alertSettings.processingTimeAlert}
                            onCheckedChange={(v) => updateAlertSetting('processingTimeAlert', v)}
                            disabled={!alertSettings.enabled}
                          />
                        </div>
                        <div className="flex items-center gap-3 ml-6">
                          <span className="text-xs text-muted-foreground w-16">Above</span>
                          <Input
                            type="number"
                            value={alertSettings.processingTimeThreshold}
                            onChange={(e) => updateAlertSetting('processingTimeThreshold', Number(e.target.value))}
                            disabled={!alertSettings.enabled || !alertSettings.processingTimeAlert}
                            className="w-20 h-7 text-sm"
                            min={10}
                            max={10000}
                          />
                          <span className="text-xs text-muted-foreground">ms</span>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            )}
          </div>
        </div>
        
        {/* Time Range Selector Row */}
        {!loading && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-muted-foreground">Time Range:</label>
            <TimeRangeSelector
              value={timeSelection}
              onChange={(newSelection) => {
                setTimeSelection(newSelection);
                setInferenceHistory([]);
              }}
              inlinePresetNav={true}
              className="flex-1 min-w-[200px]"
            />
            {historicalLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading data...
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h3 className="text-lg font-medium">Loading station data...</h3>
              <p className="text-muted-foreground mt-1">Fetching field mappings and configurations</p>
            </div>
          </div>
        ) : fieldMappings.length === 0 ? (
          <Card className="py-12">
            <CardContent className="flex flex-col items-center text-center">
              <div className="rounded-full bg-warning/10 p-3 text-warning mb-4">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium">No AI Production Mappings</h3>
              <p className="text-muted-foreground mt-1 max-w-md">
                Configure AI production field mappings for this station to see live inference data.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Current Inference Status */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-4 md:mb-6">
              {/* Status Card */}
              <Card className={`border-2 ${isOK ? 'border-success/50 bg-success/5' : 'border-destructive/50 bg-destructive/5'}`}>
                <CardHeader className="pb-1 px-3 pt-3 md:px-4 md:pt-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground">Status</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 md:px-4 md:pb-4">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${isOK ? 'bg-success' : 'bg-destructive animate-pulse'}`} />
                    <span className={`text-xl md:text-2xl font-bold ${isOK ? 'text-success' : 'text-destructive'}`}>
                      {isOK ? 'OK' : 'NOK'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Confidence Card */}
              <Card>
                <CardHeader className="pb-1 px-3 pt-3 md:px-4 md:pt-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground">Confidence</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 md:px-4 md:pb-4">
                  <div className="text-xl md:text-2xl font-bold">
                    {formatValue('inference_confidence', liveValues['inference_confidence']?.value, null)}
                  </div>
                </CardContent>
              </Card>

              {/* Processing Time Card */}
              <Card className="hidden sm:block">
                <CardHeader className="pb-1 px-3 pt-3 md:px-4 md:pt-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground">Proc. Time</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 md:px-4 md:pb-4">
                  <div className="text-xl md:text-2xl font-bold">
                    {liveValues['processing_time']?.value != null 
                      ? `${Number(liveValues['processing_time'].value).toFixed(0)}ms`
                      : '-'}
                  </div>
                </CardContent>
              </Card>

              {/* Total Parts Card */}
              <Card>
                <CardHeader className="pb-1 px-3 pt-3 md:px-4 md:pt-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground">Total Parts</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 md:px-4 md:pb-4">
                  <div className="text-xl md:text-2xl font-bold">{inferenceHistory.length}</div>
                </CardContent>
              </Card>

              {/* OK Rate Card */}
              <Card>
                <CardHeader className="pb-1 px-3 pt-3 md:px-4 md:pt-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground">OK Rate</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 md:px-4 md:pb-4">
                  <div className="text-xl md:text-2xl font-bold text-success">
                    {stats?.okRate || '0'}%
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Combined View */}
            <div className="space-y-3 md:space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
                  {/* Chart Card */}
                  <Card className="lg:col-span-2">
                    <CardHeader className="pb-2 px-3 md:px-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                            Confidence Trend
                            {isLiveMode && (
                              <Badge variant="default" className="bg-success animate-pulse text-xs">
                                Live
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs md:text-sm">Inference confidence over time</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-2 md:px-6">
                      <div className="h-[200px] sm:h-[250px] md:h-[300px]">
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis dataKey="time" fontSize={12} />
                              <YAxis 
                                fontSize={12} 
                                domain={[0, 100]}
                                label={{ value: '%', angle: -90, position: 'insideLeft' }}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: "var(--card)",
                                  border: "1px solid var(--border)",
                                  borderRadius: '8px'
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="confidence" 
                                stroke="var(--chart-3)" 
                                strokeWidth={2}
                                dot={false}
                                name="Confidence"
                              />
                              {chartData.map((entry, index) => 
                                entry.isAnomaly ? (
                                  <ReferenceDot
                                    key={index}
                                    x={entry.time}
                                    y={entry.confidence}
                                    r={6}
                                    fill="var(--destructive)"
                                    stroke="none"
                                  />
                                ) : null
                              )}
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                              <Activity className="h-12 w-12 text-muted-foreground/30 mx-auto mb-2" />
                              <p className="text-muted-foreground">Waiting for data...</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Details Panel */}
                  <div className="space-y-4">
                    {/* Part Info Card */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Current Part</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {fieldMappings.map((mapping) => {
                          const metricKey = mapping.semanticKey || mapping.datapoint?.field_name || 'unknown';
                          const config = metricTypeConfig[metricKey];
                          const value = liveValues[metricKey];
                          const unitSymbol = mapping.datapoint?.unit?.symbol || null;
                          
                          return (
                            <div key={mapping.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                {config?.icon}
                                <span>{config?.label || mapping.datapoint?.display_name || 'Unknown'}</span>
                              </div>
                              <span className="font-medium text-sm truncate max-w-[140px]" title={String(value?.value || '')}>
                                {formatValue(metricKey, value?.value, unitSymbol)}
                              </span>
                            </div>
                          );
                        })}
                        
                        {liveValues['serial_number']?.timestamp && (
                          <div className="pt-2 border-t">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>Last Update</span>
                              <span>{new Date(liveValues['serial_number'].timestamp).toLocaleTimeString()}</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Statistics Card */}
                    {stats && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Session Statistics</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Inferences</span>
                            <span className="font-medium">{stats.count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg. Confidence</span>
                            <span className="font-medium">{stats.avgConfidence.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Min Confidence</span>
                            <span className="font-medium">{stats.minConfidence.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Max Confidence</span>
                            <span className="font-medium">{stats.maxConfidence.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg. Processing</span>
                            <span className="font-medium">{stats.avgProcessingTime.toFixed(2)} ms</span>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

                {/* History Table Card */}
                <Card>
                  <CardHeader className="px-3 md:px-6">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base md:text-lg">History</CardTitle>
                        <CardDescription className="text-xs md:text-sm">
                          {inferenceHistory.length} records
                        </CardDescription>
                      </div>
                      {inferenceHistory.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-xs"
                          onClick={() => setInferenceHistory([])}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="px-2 md:px-6">
                    {inferenceHistory.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 md:py-12 text-center">
                        <Activity className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground/30 mb-3" />
                        <h3 className="text-base md:text-lg font-medium">No data yet</h3>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">
                          Results appear as parts are processed
                        </p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px] md:h-[500px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Time</TableHead>
                              <TableHead className="text-xs hidden sm:table-cell">Serial</TableHead>
                              <TableHead className="text-xs text-center">Status</TableHead>
                              <TableHead className="text-xs text-right">Conf.</TableHead>
                              <TableHead className="text-xs text-right hidden md:table-cell">Proc.</TableHead>
                              <TableHead className="text-xs hidden lg:table-cell">Model</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                              {[...inferenceHistory].reverse().map((entry, index) => (
                              <TableRow key={index} className={entry.status !== 0 ? "bg-destructive/5" : ""}>
                                <TableCell className="font-mono text-xs">
                                  {format(new Date(entry.timestamp), 'HH:mm:ss')}
                                </TableCell>
                                <TableCell className="font-mono text-xs truncate max-w-[100px] hidden sm:table-cell">
                                  {entry.serialNumber || '-'}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge 
                                    variant={entry.status === 0 ? "default" : "destructive"}
                                    className={entry.status === 0 ? "bg-success hover:bg-success/90" : ""}
                                  >
                                    {entry.status === 0 ? 'OK' : 'NOK'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {entry.confidence.toFixed(1)}%
                                </TableCell>
                                <TableCell className="text-right text-muted-foreground">
                                  {entry.processingTime.toFixed(2)} ms
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm truncate max-w-[120px]" title={entry.modelType}>
                                  {entry.modelType || '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
            </div>
          </>
        )}
    </div>
  );
};
